import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Tray,
  EnvelopeSimple,
  ChatCircle,
  DeviceMobile,
  MagnifyingGlass,
  PaperPlaneTilt,
  ArrowClockwise,
  WarningCircle,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TABS = [
  { key: "emails", label: "Customer Communications", icon: EnvelopeSimple },
  { key: "comments", label: "Portal Comments", icon: ChatCircle },
  { key: "texts", label: "Customer Texting", icon: DeviceMobile },
];

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

const Panel = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-zinc-800 bg-zinc-950 ${className}`}>{children}</div>
);

// ---------------------------------------------------------------- Emails
const EmailsTab = ({ search }) => {
  const [data, setData] = useState({ items: [], totals: { sent: 0, failed: 0 } });
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [compose, setCompose] = useState(null); // {to, subject, body}
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/inbox/emails", { params: { q: search, limit: 200 } })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Could not load the email log"))
      .finally(() => setLoading(false));
  }, [search]);
  useEffect(() => load(), [load]);

  const send = async () => {
    if (!compose?.to || !compose?.body?.trim()) return;
    setSending(true);
    try {
      await api.post("/inbox/emails/send", {
        to: compose.to,
        subject: compose.subject || "",
        body: compose.body,
      });
      toast.success(`Email sent to ${compose.to}`);
      setCompose(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
      <Panel className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2.5">
          <div className="text-xs text-zinc-400">
            <span className="text-emerald-400">{data.totals.sent} sent</span> ·{" "}
            <span className="text-red-400">{data.totals.failed} failed</span>
          </div>
          <Button
            size="sm"
            data-testid="inbox-email-new"
            onClick={() => setCompose({ to: "", subject: "", body: "" })}
          >
            <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" /> New email
          </Button>
        </div>
        <div className="max-h-[62vh] overflow-y-auto divide-y divide-zinc-900">
          {loading && <p className="px-4 py-6 text-sm text-zinc-500">Loading…</p>}
          {!loading && data.items.length === 0 && (
            <p className="px-4 py-6 text-sm text-zinc-500" data-testid="inbox-emails-empty">
              No emails yet.
            </p>
          )}
          {data.items.map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`inbox-email-row-${m.id}`}
              onClick={() => setSel(m)}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-900 ${
                sel?.id === m.id ? "bg-zinc-900" : ""
              }`}
            >
              {m.ok ? (
                <CheckCircle size={16} weight="fill" className="shrink-0 text-emerald-400" />
              ) : (
                <WarningCircle size={16} weight="fill" className="shrink-0 text-red-400" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-zinc-100">
                  {m.subject || "(no subject)"}
                </span>
                <span className="block truncate text-xs text-zinc-500">
                  {m.to} · {m.provider_label}
                </span>
              </span>
              <span className="shrink-0 text-[11px] text-zinc-600">{when(m.at)}</span>
            </button>
          ))}
        </div>
      </Panel>

      <Panel className="p-4">
        {compose ? (
          <div className="space-y-3" data-testid="inbox-email-compose">
            <div className="text-sm font-bold uppercase tracking-widest text-zinc-200">
              New email
            </div>
            <Input
              data-testid="inbox-email-to"
              placeholder="Recipient email"
              value={compose.to}
              onChange={(e) => setCompose({ ...compose, to: e.target.value })}
            />
            <Input
              data-testid="inbox-email-subject"
              placeholder="Subject"
              value={compose.subject}
              onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
            />
            <Textarea
              data-testid="inbox-email-body"
              rows={7}
              placeholder="Write your message…"
              value={compose.body}
              onChange={(e) => setCompose({ ...compose, body: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCompose(null)} disabled={sending}>
                Cancel
              </Button>
              <Button
                size="sm"
                data-testid="inbox-email-send"
                onClick={send}
                disabled={sending || !compose.to || !compose.body.trim()}
              >
                <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
                {sending ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        ) : sel ? (
          <div className="space-y-3" data-testid="inbox-email-detail">
            <div className="text-sm font-bold text-zinc-100">{sel.subject || "(no subject)"}</div>
            <div className="text-xs text-zinc-400">
              To <span className="text-zinc-200">{sel.to}</span> · {when(sel.at)}
            </div>
            <div
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                sel.ok
                  ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : "border border-red-500/40 bg-red-500/10 text-red-300"
              }`}
            >
              {sel.ok ? "Delivered" : "Failed"} · {sel.provider_label}
            </div>
            {sel.error && <p className="text-xs text-red-300">{sel.error}</p>}
            <Button
              size="sm"
              data-testid="inbox-email-reply"
              onClick={() =>
                setCompose({
                  to: sel.to,
                  subject: sel.subject?.startsWith("Re:") ? sel.subject : `Re: ${sel.subject || ""}`,
                  body: "",
                })
              }
            >
              <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" /> Reply by email
            </Button>
          </div>
        ) : (
          <p className="py-10 text-center text-sm text-zinc-500">
            Pick an email to see its status, or start a new one.
          </p>
        )}
      </Panel>
    </div>
  );
};

// ------------------------------------------------------------- Portal comments
const CommentsTab = ({ search }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/inbox/portal-comments", { params: { q: search } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Could not load portal comments"))
      .finally(() => setLoading(false));
  }, [search]);
  useEffect(() => load(), [load]);

  const openThread = (t) => {
    setSel(t);
    setThread([]);
    api
      .get("/inbox/portal-comments/thread", {
        params: { subject_type: t.subject_type, subject_id: t.subject_id },
      })
      .then(({ data }) => setThread(data.items || []))
      .catch(() => toast.error("Could not load the conversation"));
  };

  const send = async () => {
    if (!sel || !reply.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/inbox/portal-comments/reply", {
        subject_type: sel.subject_type,
        subject_id: sel.subject_id,
        body: reply,
      });
      setThread(data.messages || []);
      setReply("");
      toast.success("Reply sent to the customer");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
      <Panel className="max-h-[66vh] overflow-y-auto divide-y divide-zinc-900">
        {loading && <p className="px-4 py-6 text-sm text-zinc-500">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="px-4 py-6 text-sm text-zinc-500" data-testid="inbox-comments-empty">
            No portal comments yet.
          </p>
        )}
        {items.map((t) => (
          <button
            key={`${t.subject_type}:${t.subject_id}`}
            type="button"
            data-testid={`inbox-comment-row-${t.subject_id}`}
            onClick={() => openThread(t)}
            className={`flex w-full flex-col gap-0.5 px-4 py-3 text-left transition-colors hover:bg-zinc-900 ${
              sel?.subject_id === t.subject_id ? "bg-zinc-900" : ""
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-zinc-100">
                {t.customer_name || "Customer"}
              </span>
              {t.unread > 0 && (
                <span className="rounded-full bg-indigo-500 px-1.5 text-[10px] font-bold text-white">
                  {t.unread}
                </span>
              )}
              <span className="ml-auto text-[10px] text-zinc-600">{t.subject_code}</span>
            </span>
            <span className="truncate text-xs text-zinc-500">
              {t.last_author === "staff" ? "You: " : ""}
              {t.last_body}
            </span>
          </button>
        ))}
      </Panel>

      <Panel className="flex max-h-[66vh] flex-col">
        {sel ? (
          <>
            <div className="border-b border-zinc-800 px-4 py-2.5 text-sm font-semibold text-zinc-100">
              {sel.customer_name || "Customer"} ·{" "}
              <span className="text-zinc-500">{sel.subject_code}</span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="inbox-comment-thread">
              {thread.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    m.author === "staff"
                      ? "ml-auto bg-indigo-500/20 text-indigo-100"
                      : "bg-zinc-900 text-zinc-200"
                  }`}
                >
                  <div className="mb-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
                    {m.author_name || (m.author === "staff" ? "Staff" : "Customer")} · {when(m.created_at)}
                  </div>
                  {m.body}
                </div>
              ))}
            </div>
            <div className="flex items-end gap-2 border-t border-zinc-800 p-3">
              <Textarea
                data-testid="inbox-comment-reply"
                rows={2}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Reply to the customer…"
                className="flex-1"
              />
              <Button
                data-testid="inbox-comment-send"
                onClick={send}
                disabled={sending || !reply.trim()}
              >
                <PaperPlaneTilt size={15} weight="bold" />
              </Button>
            </div>
          </>
        ) : (
          <p className="py-10 text-center text-sm text-zinc-500">
            Pick a conversation to read and reply.
          </p>
        )}
      </Panel>
    </div>
  );
};

// ------------------------------------------------------------- Texting
const TextsTab = ({ search }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/sms/threads")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => toast.error("Could not load texts"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => load(), [load]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    if (!q) return items;
    return items.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.phone || "").includes(q) ||
        (t.last_body || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const openThread = (t) => {
    setSel(t);
    setThread([]);
    api
      .get("/sms/thread", { params: { phone: t.phone } })
      .then(({ data }) => setThread(data.items || []))
      .catch(() => toast.error("Could not load the conversation"));
    if (t.unread > 0)
      api.post("/sms/read", null, { params: { phone: t.phone } }).then(load).catch(() => {});
  };

  const send = async () => {
    if (!sel || !reply.trim()) return;
    setSending(true);
    try {
      await api.post("/sms/send", {
        to: sel.phone,
        body: reply,
        entity: sel.customer_id ? "customer" : "",
        entity_id: sel.customer_id || "",
      });
      setReply("");
      const { data } = await api.get("/sms/thread", { params: { phone: sel.phone } });
      setThread(data.items || []);
      toast.success(`Text sent to ${sel.name || sel.phone}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the text");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
      <Panel className="max-h-[66vh] overflow-y-auto divide-y divide-zinc-900">
        {loading && <p className="px-4 py-6 text-sm text-zinc-500">Loading…</p>}
        {!loading && filtered.length === 0 && (
          <p className="px-4 py-6 text-sm text-zinc-500" data-testid="inbox-texts-empty">
            No texts yet.
          </p>
        )}
        {filtered.map((t) => (
          <button
            key={t.phone_key}
            type="button"
            data-testid={`inbox-text-row-${t.phone_key}`}
            onClick={() => openThread(t)}
            className={`flex w-full flex-col gap-0.5 px-4 py-3 text-left transition-colors hover:bg-zinc-900 ${
              sel?.phone_key === t.phone_key ? "bg-zinc-900" : ""
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="truncate text-sm font-semibold text-zinc-100">
                {t.name || t.phone}
              </span>
              {t.unread > 0 && (
                <span className="rounded-full bg-indigo-500 px-1.5 text-[10px] font-bold text-white">
                  {t.unread}
                </span>
              )}
              <span className="ml-auto text-[10px] text-zinc-600">{when(t.last_at)}</span>
            </span>
            <span className="truncate text-xs text-zinc-500">
              {t.last_dir === "outbound" ? "You: " : ""}
              {t.last_body}
            </span>
          </button>
        ))}
      </Panel>

      <Panel className="flex max-h-[66vh] flex-col">
        {sel ? (
          <>
            <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-2.5 text-sm font-semibold text-zinc-100">
              {sel.customer_id ? (
                <Link to={`/customers/${sel.customer_id}`} className="hover:underline">
                  {sel.name || sel.phone}
                </Link>
              ) : (
                sel.name || sel.phone
              )}
              <span className="font-mono-data ml-auto text-xs text-zinc-500">{sel.phone}</span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-4" data-testid="inbox-text-thread">
              {thread.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    m.direction === "outbound"
                      ? "ml-auto bg-indigo-500/20 text-indigo-100"
                      : "bg-zinc-900 text-zinc-200"
                  }`}
                >
                  <div className="mb-0.5 text-[10px] uppercase tracking-widest text-zinc-500">
                    {m.direction === "outbound" ? "Sent" : "Received"} · {when(m.sent_at)}
                    {m.status && m.direction === "outbound" ? ` · ${m.status}` : ""}
                  </div>
                  {m.body}
                </div>
              ))}
            </div>
            <div className="flex items-end gap-2 border-t border-zinc-800 p-3">
              <Textarea
                data-testid="inbox-text-reply"
                rows={2}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Text the customer…"
                className="flex-1"
              />
              <Button data-testid="inbox-text-send" onClick={send} disabled={sending || !reply.trim()}>
                <PaperPlaneTilt size={15} weight="bold" />
              </Button>
            </div>
          </>
        ) : (
          <p className="py-10 text-center text-sm text-zinc-500">
            Pick a conversation to read and reply.
          </p>
        )}
      </Panel>
    </div>
  );
};

export default function Inbox() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = TABS.some((t) => t.key === tab) ? tab : "emails";
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer.current);
  }, [search]);

  useEffect(() => setSearch(""), [active]);

  return (
    <div className="grain min-h-screen" data-testid="inbox-page">
      <AppHeader title="Inbox" />
      <main className="relative z-10 px-4 pb-10 pt-6">
        <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-300">
          <Tray size={20} weight="bold" />
        </span>
        <div>
          <h1 className="text-xl font-bold text-zinc-100">Inbox</h1>
          <p className="text-xs text-zinc-500">
            Every email, portal comment and text with your customers in one place.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map((t) => {
          const on = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`inbox-tab-${t.key}`}
              onClick={() => navigate(`/inbox/${t.key}`)}
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${
                on
                  ? "bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-400/40"
                  : "border border-zinc-800 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
              }`}
            >
              <t.icon size={15} weight="bold" /> {t.label}
            </button>
          );
        })}
        <div className="relative ml-auto w-full sm:w-64">
          <MagnifyingGlass
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <Input
            data-testid="inbox-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="pl-9"
          />
        </div>
      </div>

      {active === "emails" && <EmailsTab search={debounced} />}
      {active === "comments" && <CommentsTab search={debounced} />}
      {active === "texts" && <TextsTab search={debounced} />}
        </div>
      </main>
    </div>
  );
}
